import {
  DAY_MS,
  DEFAULT_WEEK_START_DAY,
  WEEK_MS,
  calendarWeeksAgo,
  computeWeekStreak,
  type Session,
  type WeekStreak,
} from "@heraion/shared";

// Session-count summaries behind the Progress footer (lifted from mobile's
// progressScreenMath). The frequency bar chart + the total/7d/30d/last
// stats read these.

export const FREQ_WEEKS = 12;

export type SessionSummary = {
  total: number;
  last7d: number;
  last30d: number;
  lastSession: Session | null;
};

export function computeSessionSummary(
  sessions: Session[],
  nowMs: number,
): SessionSummary {
  let last7d = 0;
  let last30d = 0;
  let lastSession: Session | null = null;
  let lastT = 0;
  for (const s of sessions) {
    const at = new Date(s.startedAt).getTime();
    if (nowMs - at <= WEEK_MS) last7d++;
    if (nowMs - at <= 30 * DAY_MS) last30d++;
    if (at > lastT) {
      lastT = at;
      lastSession = s;
    }
  }
  return { total: sessions.length, last7d, last30d, lastSession };
}

// Widened to the one field it reads (CO-9): the coach detail buckets the
// athlete's session-results (which carry only a timestamp), while the mobile
// host keeps passing full Session rows — structurally compatible, no change.
// PG-18: calendar weeks from the athlete's weekStartDay, the last bar being
// the current (partial) week.
export function computeWeeklyCounts(
  sessions: ReadonlyArray<Pick<Session, "startedAt">>,
  nowMs: number,
  weeks: number = FREQ_WEEKS,
  weekStartDay: number = DEFAULT_WEEK_START_DAY,
): number[] {
  const out = new Array<number>(weeks).fill(0);
  for (const s of sessions) {
    const ago = calendarWeeksAgo(
      new Date(s.startedAt).getTime(),
      nowMs,
      weekStartDay,
    );
    // Also drops an unparseable date (NaN fails both sides).
    if (!(ago >= 0 && ago < weeks)) continue;
    const weekIdx = weeks - 1 - ago;
    out[weekIdx] = (out[weekIdx] ?? 0) + 1;
  }
  return out;
}

/** Everything Progress reads off the sessions list; empty while it loads. */
export type SessionsRecap = {
  summary: SessionSummary | null;
  weeklyCounts: number[];
  weekStreak: WeekStreak | null;
};

// One step from the sessions to the stats, the weekly counts (PG-18) and the
// streak (PG-58): mobile's Progress and useSessionsSummary both call it.
export function summarizeSessions(
  sessions: Session[] | null,
  nowMs: number,
  weekStartDay: number | undefined,
): SessionsRecap {
  if (sessions === null) {
    return { summary: null, weeklyCounts: [], weekStreak: null };
  }
  return {
    summary: computeSessionSummary(sessions, nowMs),
    weeklyCounts: computeWeeklyCounts(sessions, nowMs, undefined, weekStartDay),
    weekStreak: computeWeekStreak(sessions, nowMs, weekStartDay),
  };
}
